<template>
  <div
    class="product-card border rounded-lg flex flex-col justify-between gap-1 h-full w-full
    text-EXPONDO-black relative overflow-hidden"
    :data-bloomreach-id="testProductIdentifier"
  >
    <CustomLink
      data-testid="ProductCardItem"
      :type="LINK_TYPE.NoStyle"
      :url="productUrl"
    >
      <div
        class="px-2.5 pt-2.5"
        @click="onClick"
      >
        <div class="product-card__top h-9">
          <ProductCardBadges
            v-if="showCardBadge"
            :tags="tags"
          />
          <ProductCardWishlist
            v-if="false"
          />
        </div>
        <ProductCardGallery
          :image="image"
          :alt-text="imageAlt"
        />
        <div class="product-card__brand p-2.5">
          <div class="h-5.5">
            <ProductCardBrand
              v-if="brand"
              :brand="brand"
            />
          </div>
        </div>
        <div class="product-card__reviews h-7">
          <ProductAverageReview
            v-if="rating"
            :rating="ratingToShow"
            :is-card-reviews="true"
          />
        </div>
        <div
          class="flex items-center h-22 group"
          @mouseenter="handleTitleHover"
          @mouseleave="handleMouseLeave"
        >
          <p class="product-card__name line-clamp-3 text-lg leading-6 font-semibold">
            {{ title }}
          </p>
          <ProductCardOverlay
            :key-facts="keyFacts"
            :link="productUrl"
          />
        </div>
        <div class="flex items-center">
          <div
            v-if="dimensions.hasDimensions"
            class="text-sm flex items-center justify-between w-full py-1"
          >
            <div>
              {{ $t('Dimensions') }}
            </div>
            <div class="font-bold">
              {{ dimensionsValue }}
            </div>
          </div>
        </div>
      </div>
    </CustomLink>
    <EnergyRating
      v-if="energyRating"
      class="px-2.5"
      :energy-rating="energyRating"
    />
    <div class="flex flex-col gap-1 p-2.5 bg-EXPONDO-grey-5 rounded-b-lg">
      <div class="flex flex-col gap-1">
        <CustomLink
          :url="productUrl"
          :type="LINK_TYPE.NoStyle"
        >
          <div
            @click="onClick"
          >
            <ProductCardPricing
              class="product-card__pricing"
              :full-product-price="fullProductPrice"
              :discounted-price="discountedPrice"
              :is-discounted="isDiscounted"
              :last30-days-price="last30DaysPrice"
            />
          </div>
        </CustomLink>
        <div class="h-5">
          <LimitedTimeOffer v-if="discountedPrice" />
        </div>
        <LowestPriceGuaranteed
          :price-guarantee-url="priceGuaranteeUrl"
        />
        <StockInformation
          :full-product-price="fullProductPrice"
          :stock-count="stockCount"
          :is-in-stock="isInStock"
          :is-test-product="isTestProductItem"
        />
        <div class="h-10">
          <ProductCardDelivery
            v-if="showDeliveryTiming || testProductIdentifier"
            :delivery-date="deliveryDate"
            :availability-date="availabilityDate"
          />
        </div>
      </div>

      <AddToCartButton
        class="w-full"
        :link="productUrl"
        :sku="sku"
        :name="title"
        :image="image"
        :full-product-price="fullProductPrice"
        :is-in-stock="isInStock"
        :stock="stockCount"
        @click="onClick"
      />
    </div>
  </div>
</template>

<script lang="ts">

import type { ProductTag } from '~~/types/product/attribute/ProductTag';
import buildProductRating from '~~/helpers/product/buildProductRating';

import type ProductDimensionsInterface from '~~/types/product/attribute/ProductDimensions';
import { isTestProduct } from '~~/helpers/product/isTestProduct';
import type { EnergyRatingDetails } from '~~/types/components/EnergyRating/EnergyRatingDetails';
import type { ProductCategories } from '~~/types/product/ProductCategories';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { getFinalPriceFromAmounts } from '~~/helpers/product/getPriceInfo';
import { addNativeRoutingToProductUrl } from '~~/helpers/routes/nativeRouting/transformProductsForNativeRouting';

export default defineComponent({
  name: 'ProductCard',

  props: {
    title: {
      type: String,
      required: true,
    },
    link: {
      type: String,
      required: true,
    },
    fullProductPrice: {
      type: Number,
      required: true,
    },
    discountedPrice: {
      type: Number,
      default: null,
    },
    isDiscounted: {
      type: Boolean,
      default: false,
    },
    last30DaysPrice: {
      type: Number,
      default: null,
    },
    sku: {
      type: String,
      required: true,
    },
    reviewsCount: {
      type: Number,
      default: 0,
    },
    rating: {
      type: Number,
      default: 0,
    },
    image: {
      type: String,
      default: '',
    },
    tags: {
      type: Array as PropType<ProductTag[]>,
      default: () => {

        return [];

      },
    },
    brand: {
      type: String,
      default: '',
    },
    dimensions: {
      type: Object as PropType<ProductDimensionsInterface>,
      default: () => {

        return { hasDimensions: false };

      },
    },
    priceGuaranteeUrl: {
      type: String,
      required: true,
    },
    stockCount: {
      type: Number,
      required: true,
    },
    parcelDeliveryDate: {
      type: String,
      default: '',
    },
    freightDeliveryDate: {
      type: String,
      default: '',
    },
    isFreight: {
      type: Boolean,
      default: false,
    },
    keyFacts: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    releaseDate: {
      type: String,
      required: true,
    },
    energyRating: {
      type: Object as PropType<Maybe<EnergyRatingDetails>>,
      default: (): Maybe<EnergyRatingDetails> => null,
    },
    currency: {
      type: String,
      required: true,
    },
    categories: {
      type: [Object, String] as PropType<ProductCategories>,
      required: true,
    },
    productBoxTypeTag: {
      type: String,
      required: true,
    },
  },
  emits: ['product-clicked', 'title-hovered', 'title-leave'],
  setup(props, { emit }) {

    const ratingToShow = computed(() => {

      return buildProductRating({
        averageRating: props.rating,
        count: props.reviewsCount,
      });

    });

    const isInStock = computed(() => props.stockCount > 0);
    const finalPrice = computed(() => getFinalPriceFromAmounts(props.discountedPrice, props.fullProductPrice));
    const showCardBadge = computed(() => props.tags.length);
    const deliveryDate = computed(() => props.isFreight ? props.freightDeliveryDate : props.parcelDeliveryDate);
    const showDeliveryTiming = computed(() => deliveryDate.value && isInStock.value);

    const i18n = useI18n();
    const { countryCode } = useLocalization();
    const dimensionsValue = computed(() => i18n.t('Dimensions Value', {
      length: props.dimensions.length,
      width: props.dimensions.width,
      height: props.dimensions.height,
    }));

    const handleTitleHover = () => {

      emit('title-hovered', props.sku);

    };

    const handleMouseLeave = () => {

      emit('title-leave');

    };

    const isTestProductItem = computed(() => isTestProduct(props.sku));
    const testProductIdentifier = computed(() => isTestProductItem.value ? 'test-product' : undefined);

    const { calculateAvailabilityDate } = useProductAvailabilityDate(props.releaseDate);
    const availabilityDate = computed(() => isTestProductItem.value ? calculateAvailabilityDate() : '');

    const { trackClickProductBox } = useGtmEvents();

    const onClick = () => {

      emit('product-clicked');
      trackClickProductBox({
        boxType: props.productBoxTypeTag,
        product: {
          sku: props.sku,
          name: props.title,
          currency: props.currency,
          discountedPrice: finalPrice.value,
          brand: props.brand,
          price: props.fullProductPrice,
          categories: props.categories,
        },
      });

    };
    const productUrl = computed(() => {

      return addNativeRoutingToProductUrl(props.link, countryCode.value);

    });

    const imageAlt = computed(() => {

      return `${props.title} - ${i18n.t('Product Image')}`;

    });

    return {
      ratingToShow,
      finalPrice,
      showCardBadge,
      deliveryDate,
      showDeliveryTiming,
      onClick,
      dimensionsValue,
      isInStock,
      imageAlt,
      handleTitleHover,
      handleMouseLeave,
      testProductIdentifier,
      isTestProductItem,
      availabilityDate,
      LINK_TYPE,
      productUrl,
    };

  },
});


</script>

<style lang="scss" scoped>
$product-image-height: calc(var(--product-card-height) * 0.55);
$single-line-section-height: 40px;
$double-line-section-height: 50px;

.product-card {
  @include hover-shadow;

  a {
    text-decoration: none;
  }

  &__top {
    display: flex;
    justify-content: space-between;
  }

  &__image {
    height: $product-image-height;
  }

  &__ratings {
    height: $single-line-section-height;
  }

  &__pricing {
    text-align: center;
  }
}
</style>
